<template>
    <tailwind-layout title="Rent Payments: Property Transactions">
        <template #header>
            <div class="px-4 sm:px-6 lg:px-8 mx-auto hidden lg:flex justify-between items-center"> <!-- Block for lg and larger -->
                <div>
                    <h1 class="font-semibold text-2xl leading-tight">Rent Payments: Property Transactions</h1>
                    <h2 class="text-2xl font-bold mt-5">{{ property.address_1 }}</h2>
                    <p class="text-sm font-medium underline">
                        <a :href="'https://www.google.com/maps?q='+property.map_address"
                        target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5" aria-hidden="true" /></a>
                    </p>
                    <div v-if="transactions.data.length">
                        <p v-if="$page.props.user.role === 'agent'" class="text-sm font-medium  mt-3">Tenant: {{transactions.data[0]?.tenant.name}}</p>
                        <p v-if="$page.props.user.role === 'applicant'" class="text-sm font-medium  mt-3">Landlord: {{transactions.data[0]?.landlord.name}}</p>
                        <p class="text-sm font-medium  mt-3">Lease Term: {{ transactions.data[0]?.lease_term ? transactions.data[0]?.lease_term?.start_end : '' }}</p>
                    </div>
                </div>
                <div class="flex items-center space-x-4">
                    <inertia-link class="whiteButton" :href="$page.props.user.role === 'applicant' ? route('my-property', { tab: 'rent-payments' }) : route('properties.show', property.id) + '?tab=rent-payments'">
                        <font-awesome-icon :icon="faChevronLeft" class="h-5 w-5 text-gray-400" aria-hidden="true" /> Back
                    </inertia-link>
                </div>
            </div>

            <div class="mb-3 px-6 lg:px-8 mx-auto flex flex-col lg:hidden"><!-- Block for sm, md, lg (buttons will stack on smaller screens) -->
                <div class="flex justify-between items-center">
                    <div>
                        <h1 class="font-semibold text-2xl leading-tight">Rent Payments: Property Transactions</h1>
                        <h2 class="text-2xl font-bold mt-5">{{ property.address_1 }}</h2>
                        <p class="text-sm font-medium underline">
                            <a :href="'https://www.google.com/maps?q='+property.map_address"
                            target="_blank">{{ property.full_address }} <font-awesome-icon :icon="faMapMarkerAlt" class="h-5 w-5" aria-hidden="true" /></a>
                        </p>
                        <div v-if="transactions.data.length">
                            <p v-if="$page.props.user.role === 'agent'" class="text-sm font-medium  mt-3">Tenant: {{transactions.data[0]?.tenant.name}}</p>
                            <p v-if="$page.props.user.role === 'applicant'" class="text-sm font-medium  mt-3">Landlord: {{transactions.data[0]?.landlord.name}}</p>
                            <p class="text-sm font-medium  mt-3">Lease Term: {{ transactions.data[0]?.lease_term ? transactions.data[0]?.lease_term?.start_end : '' }}</p>
                        </div>
                    </div>
                </div>
            </div>
        </template>
        <div class="overflow-hidden">
            <div class="relative">
                <div class="grid grid-cols-2 gap-4 mb-6 sm:flex sm:justify-items-stretch">
                    <div class="sm:w-2/12 pr-4 order-3 sm:order-1 pt-5">
                        <select v-model="form.paginate" class="mt-1 block w-full pl-3 pr-10 py-2 text-base border-gray-100 drop-shadow focus:outline-none focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 sm:text-sm rounded-md" aria-label="Number of items per page">
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                        </select>
                    </div>
                    <div class="sm:w-2/12 sm:pr-4 order-2">
                        <label for="date_from" class="block text-sm font-medium text-gray-700">
                            Date From
                        </label>
                        <input id="date_from" v-model="form.date_from" type="date" class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                    <div class="sm:w-2/12 sm:pr-4 order-2 sm:order-3">
                        <label for="date_to" class="block text-sm font-medium text-gray-700">
                            Date To
                        </label>
                        <input id="date_to" v-model="form.date_to" type="date" class="mt-1 shadow-sm focus:ring-rent-risk-blue-400 focus:border-rent-risk-blue-400 block w-full sm:text-sm border-gray-100 drop-shadow rounded-md">
                    </div>
                    <div class="sm:w-2/12 order-5 sm:order-5 flex items-start ml-auto pt-5 justify-end">
<!--                        <a target="_blank" :href="route('rented-properties.property-transactions.download', [property.id, form])" class="mt-1 sm:ml-3 inline-flex justify-center py-2 px-4 border border-transparent shadow-sm text-sm font-medium rounded-md text-gray-900 bg-rent-risk-blue-400 hover:bg-black focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-rent-risk-blue-400">-->
<!--                            Download-->
<!--                        </a>-->
                    </div>
                </div>
            </div>
            <div class="rounded-md lg:shadow shadow-none overflow-x-auto">
                <div class="flex flex-col">
                    <div class="overflow-x-auto">
                        <div class="align-middle inline-block min-w-full">
                            <div class="shadow overflow-hidden lg:border">
                                <input ref="sort_by" type="hidden" v-model="form.sort_by" />
                                <input ref="sort_direction" type="hidden" v-model="form.sort_direction" />
                                <table class="min-w-full divide-y divide-gray-300">
                                    <thead class="bg-blue-50">
                                    <tr>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span @click="sortTable('amount')">
                                                Amount
                                                <icon v-show="form.sort_by === 'amount'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'amount'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Convenience Fee
                                        </th>
                                        <th v-if="$page.props.user.role === 'applicant'" scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Payment Method
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            {{$page.props.user.role === 'agent' ? 'My Payout' : 'Total'}}
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span @click="sortTable('created_at')">
                                                Date
                                                <icon v-show="form.sort_by === 'created_at'" :name="form.sort_direction_icon" />
                                                <icon v-if="!form.sort_by || form.sort_by !== 'created_at'" :name="'sort'" />
                                            </span>
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Detail
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Lease Term
                                        </th>
                                        <th scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            <span v-if="$page.props.user.role === 'agent'">Tenant</span> Payment Status
                                        </th>
                                        <th v-if="$page.props.user.role === 'agent'" scope="col" class="px-3 py-3.5 text-sm font-semibold text-gray-900 text-left">
                                            Landlord Payment Status
                                        </th>
                                    </tr>
                                    </thead>
                                    <tbody>
                                    <tr v-for="(transaction, index) in transactions.data" :key="transaction.id"
                                        :class="[(index % 2 === 0 ? 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer' : 'bg-gray-50 border-b hover:bg-yellow-50 hover:cursor-pointer')]"
                                    >
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.amount }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.stripe_fee }}
                                        </td>
                                        <td v-if="$page.props.user.role === 'applicant'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.payment_type }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            ${{ transaction.total }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.date }}
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            <ul v-if="transaction.payments.length > 1" class="mt-3 list-inside text-sm">
                                                <li v-for="(payment, key) in transaction.payments" :key="key">
                                                    {{ payment.month !== null ? payment.month.month_name : payment.one_time_charge.title }}
                                                </li>
                                            </ul>
                                            <span v-else>
                                                {{ transaction.payments[0].month !== null ? transaction.payments[0].month.month_name : transaction.payments[0].one_time_charge.title }}
                                            </span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900" data-test="lease-term">
                                            <a :href="route('rented-properties.payments', transaction.lease_term.id)" class="text-blue-600 underline">{{ transaction.lease_term.start_end }}</a>
                                            <span class="ml-1 text-gray-500">({{ transaction.lease_term.status }})</span>
                                        </td>
                                        <td class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.tenant_payment_status }}
                                        </td>
                                        <td v-if="$page.props.user.role === 'agent'" class="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900">
                                            {{ transaction.landlord_payment_status }}
                                        </td>
                                    </tr>
                                    <tr v-show="!transactions.data.length">
                                        <td :colspan="$page.props.user.role === 'agent' ? 10 : 9" class="px-6 py-4 whitespace-nowrap text-dark-gray-400 text-sm font-medium text-center">
                                            No transactions were found.
                                        </td>
                                    </tr>
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <pagination class="mt-6" :links="transactions.meta.links" :results="transactions.meta" :type="'transactions'"/>
        </div>
    </tailwind-layout>
</template>

<script>
import Icon from '@/Shared/Icon.vue'
import TailwindLayout from '@/Layouts/TailwindLayout.vue'
import throttle from 'lodash/throttle'
import mapValues from 'lodash/mapValues'
import Pagination from '@/Shared/Pagination.vue'
import pickBy from 'lodash/pickBy'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import { faChevronLeft, faMapMarkerAlt } from '@fortawesome/pro-solid-svg-icons'

export default {
    components: {
        Icon,
        Pagination,
        TailwindLayout,
        FontAwesomeIcon
    },
    props: {
        filters: Object,
        transactions: Object,
        property: Object,
    },
    data() {
        return {
            form: {
                date_from: this.filters.date_from,
                date_to: this.filters.date_to,
                search: this.filters.search,
                trashed: this.filters.trashed,
                paginate: (this.filters.paginate)?this.filters.paginate:25,
                sort_by: this.filters.sort_by,
                sort_direction_icon : (this.filters.sort_direction === 'desc')?'sort-up':'sort-down'
            },
            faChevronLeft,
        }
    },
    watch: {
        form: {
            deep: true,
            handler: throttle(function() {
                this.$inertia.get(this.route('rented-properties.property-transactions', this.property.id), pickBy(this.form), { preserveState: true })
            }, 150),
        },
    },
    methods: {
        sortTable(column) {
            this.form.sort_by = column
            this.form.sort_direction = (this.form.sort_direction === 'desc')?'asc':'desc'
            this.form.sort_direction_icon = (this.form.sort_direction === 'desc')?'sort-up':'sort-down'
        },
        reset() {
            this.form = mapValues(this.form, () => null)
        },
    },
}
</script>
